import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, errorMessage } from "../../lib/api";
import { GROUP_LABELS } from "../../lib/fieldMeta";
import { useLang } from "../../lib/i18n";

const EMPTY = { label_en: "", label_ar: "", type: "text", group: "business", dir: "ltr", editable_by: "employee" };
const TYPES = { text: "Short text", textarea: "Long text", url: "Link", tel: "Phone", email: "Email" };
const DIRS = { ltr: "Left to right (English)", rtl: "Right to left (Arabic)" };
const EDITORS = { employee: "Employee can edit", admin: "Admin only" };

const Pick = ({ id, label, value, options, onChange }) => {
  const { t } = useLang();
  return (
  <div className="space-y-1.5">
    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-12 rounded-xl" data-testid={`custom-field-${id}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        {Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`custom-field-${id}-${k}`}>{t(v)}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
  );
};

export const CustomFieldDialog = ({ open, onOpenChange, field, onSaved }) => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(field ? { ...EMPTY, ...field } : EMPTY); }, [open, field]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k]]));
    try {
      const { data } = field ? await api.put(`/admin/settings/fields/custom/${field.key}`, body)
        : await api.post("/admin/settings/fields/custom", body);
      onSaved(data.fields);
      toast.success(t(field ? "Field updated" : "Field added"));
      onOpenChange(false);
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl" data-testid="custom-field-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">{t(field ? "Edit field" : "New field")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cf-en" className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("Label (English)")} *</Label>
              <Input id="cf-en" dir="ltr" required maxLength={40} value={form.label_en} onChange={(e) => set("label_en")(e.target.value)}
                className="h-12 rounded-xl" data-testid="custom-field-label-en" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cf-ar" className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("Label (Arabic)")} *</Label>
              <Input id="cf-ar" dir="rtl" required maxLength={40} value={form.label_ar} onChange={(e) => set("label_ar")(e.target.value)}
                className="h-12 rounded-xl font-arabic text-right" data-testid="custom-field-label-ar" />
            </div>
            <Pick id="type" label={t("Type")} value={form.type} options={TYPES} onChange={set("type")} />
            <Pick id="group" label={t("Section")} value={form.group}
              options={Object.fromEntries(Object.entries(GROUP_LABELS).map(([k, v]) => [k, v[lang]]))} onChange={set("group")} />
            <Pick id="dir" label={t("Text direction")} value={form.dir} options={DIRS} onChange={set("dir")} />
            <Pick id="editable" label={t("Who can edit")} value={form.editable_by} options={EDITORS} onChange={set("editable_by")} />
          </div>
          <p className="text-xs text-slate-500">{t("Custom fields show on the card page and in forms. They are not added to the vCard.")}</p>
          <button type="submit" disabled={saving} data-testid="custom-field-save"
            className="btn-press min-h-[50px] w-full rounded-2xl bg-slate-900 font-semibold text-white disabled:opacity-60">
            {saving ? t("Saving…") : t(field ? "Save field" : "Add field")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
